/**
 * Timeline Service
 * ----------------
 * Fetches and adapts the forensic timeline from the Watch API.
 *
 * The Watch API provides a pre-computed timeline via:
 *   GET /sessions/{id}/timeline?buckets={n}
 *
 * Response shape:
 *   {
 *     duration_ms: number,
 *     bucket_ms:   number,
 *     buckets:     Array<{ t: number, density: number }>,
 *     markers:     Array<{ t: number, type: string, label: string, frame_id: number }>
 *   }
 *
 * Bucket density is already normalized 0.0–1.0 by the backend using
 * event weights (detection=10, command=5, keystroke=2, click=1, frame=1).
 * No local computation needed — we just map to the shape the player store
 * and components expect.
 *
 * Marker types: "detection" | "command" | "keystroke"
 */

import { apiClient } from "./apiClient";

// ---------------------------------------------------------------------------
// API call
// ---------------------------------------------------------------------------

/**
 * Fetches the pre-computed timeline for a session.
 *
 * @param {string} sessionId
 * @param {number} [numBuckets=100]
 * @returns {Promise<object>} Raw API response
 */
export async function fetchTimeline(sessionId, numBuckets = 100) {
  return apiClient(`/sessions/${sessionId}/timeline?buckets=${numBuckets}`);
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

/**
 * Converts the API timeline response into the heatmap format expected by
 * the player store (activityHeatmap) and ProgressBar / ActivityTab.
 *
 * Output shape per bucket:
 *   {
 *     timestamp:   number,   // start of bucket in seconds
 *     intensity:   number,   // normalized density 0.0–1.0
 *     active:      boolean,  // true if density > 0
 *     eventCount:  number,   // estimated — bucket_ms converted to event weight
 *   }
 *
 * @param {object} timeline - Raw API response from fetchTimeline()
 * @returns {Array<object>}
 */
export function adaptTimelineToHeatmap(timeline) {
  if (!timeline?.buckets?.length) return [];

  return timeline.buckets.map((bucket) => ({
    timestamp:  bucket.t / 1000,       // ms → seconds
    intensity:  bucket.density,
    active:     bucket.density > 0,
    eventCount: Math.round(bucket.density * 10), // approximate for display
  }));
}

/**
 * Derives idle periods (gaps) from the heatmap.
 * A gap is a contiguous run of inactive buckets lasting at least
 * minGapSeconds.
 *
 * @param {Array<object>} heatmap     - Output of adaptTimelineToHeatmap()
 * @param {number}        [minGapSeconds=2]
 * @returns {Array<{ start, end, duration }>}
 */
export function calculateIdlePeriods(heatmap, minGapSeconds = 2) {
  const gaps = [];
  let current = null;

  heatmap.forEach((item) => {
    if (!item.active) {
      if (!current) current = { start: item.timestamp };
    } else if (current) {
      current.end      = item.timestamp;
      current.duration = current.end - current.start;
      if (current.duration >= minGapSeconds) gaps.push(current);
      current = null;
    }
  });

  if (current) {
    const last = heatmap[heatmap.length - 1];
    current.end      = last.timestamp;
    current.duration = current.end - current.start;
    if (current.duration >= minGapSeconds) gaps.push(current);
  }

  return gaps.sort((a, b) => b.duration - a.duration);
}

/**
 * Derives active periods from the heatmap.
 * An active period is a contiguous run of buckets with intensity above
 * minIntensity.
 *
 * @param {Array<object>} heatmap
 * @param {number}        [minIntensity=0.1]
 * @returns {Array<{ start, end, duration, avgIntensity, maxIntensity, eventCount }>}
 */
export function calculateActivePeriods(heatmap, minIntensity = 0.1) {
  const periods = [];
  let current   = null;

  heatmap.forEach((item) => {
    if (item.active && item.intensity >= minIntensity) {
      if (!current) {
        current = {
          start:        item.timestamp,
          maxIntensity: item.intensity,
          avgIntensity: item.intensity,
          eventCount:   item.eventCount,
          samples:      1,
        };
      } else {
        current.maxIntensity  = Math.max(current.maxIntensity, item.intensity);
        current.avgIntensity  =
          (current.avgIntensity * current.samples + item.intensity) /
          (current.samples + 1);
        current.eventCount   += item.eventCount;
        current.samples++;
      }
    } else if (current) {
      current.end      = item.timestamp;
      current.duration = current.end - current.start;
      if (current.duration >= 1) periods.push(current);
      current = null;
    }
  });

  return periods.sort((a, b) => b.avgIntensity - a.avgIntensity);
}

// ---------------------------------------------------------------------------
// Main wrapper (called by playerStore.loadActivityData)
// ---------------------------------------------------------------------------

/**
 * Fetches the timeline and returns all derived data needed by the player
 * store and UI components.
 *
 * @param {string} sessionId
 * @param {number} [numBuckets=100]
 * @returns {Promise<{
 *   heatmap:   Array,
 *   gaps:      Array,
 *   periods:   Array,
 *   markers:   Array,
 *   stats:     object
 * }>}
 */
export async function getSessionTimeline(sessionId, numBuckets = 100) {
  const timeline = await fetchTimeline(sessionId, numBuckets);

  const heatmap = adaptTimelineToHeatmap(timeline);
  const gaps    = calculateIdlePeriods(heatmap);
  const periods = calculateActivePeriods(heatmap);

  const activeCount  = heatmap.filter((b) => b.active).length;
  const activePercent = heatmap.length
    ? ((activeCount / heatmap.length) * 100).toFixed(1)
    : "0.0";

  return {
    heatmap,
    gaps,
    periods,
    markers:     timeline.markers  ?? [],
    duration_ms: timeline.duration_ms,
    bucket_ms:   timeline.bucket_ms,
    stats: {
      totalBuckets:   heatmap.length,
      activeBuckets:  activeCount,
      activePercent,
      durationSeconds: (timeline.duration_ms ?? 0) / 1000,
    },
  };
}

// ---------------------------------------------------------------------------
// Legacy export alias
//
// playerStore still calls getTimelineHeatmap(baseUrl, jobId, duration).
// This wrapper keeps the old signature working until playerStore is updated.
// ---------------------------------------------------------------------------

/**
 * @deprecated Use getSessionTimeline(sessionId) instead.
 */
export async function getTimelineHeatmap(_baseUrl, sessionId, _duration) {
  return getSessionTimeline(sessionId);
}